import { jsonToFormData } from '../utils/object';
import { pcbffConnector } from './connector';
import { config } from '../config';

function handleErrorService(e) {
  console.error('API Error:', e);

  // Case 1: Server ตอบกลับมา (มี response)
  if (e.response) {
    return {
      status: 'ERROR',
      code: e.response.data?.code || e.response.status || '',
      message: e.response.data?.message || e.message || 'Server error',
      type: 'SERVER_ERROR'
    };
  }

  // Case 2: Request ถูกส่งแต่ไม่ได้ response (Network Error, Timeout)
  if (e.request) {
    return {
      status: 'ERROR',
      code: e.code || 'NETWORK_ERROR',
      message: e.message || 'Cannot connect to server',
      type: 'NETWORK_ERROR'
    };
  }

  // Case 3: Error ตอน setup request
  return {
    status: 'ERROR',
    code: 'REQUEST_SETUP_ERROR',
    message: e.message || 'Request configuration error',
    type: 'REQUEST_SETUP_ERROR'
  };
}

export const userToAuthHeader = (user) => {
  if (['pruservices', 'pruservicesV2'].includes(user?.system)) {
    if (user?.userAuthToken) {
      return {
        'pru-user-token': user.userAuthToken,
      }
    }
    return {
      'pru-client-id': user.clientId,
    };
  } else if (user?.system === 'line') {
    return {
      authorization: user.token,
    };
  } else {
    return {};
  }
}

export const getAccountMapping = async user => {
  return pcbffConnector.get(`/auth/account-mapping`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const decodePolicyAPI = async (encryptedPolicy) => {
  return pcbffConnector.post(`/auth/sso/decode-policy`, { encryptedPolicy })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getPolicyDetail = async (user, policyCertNo) => {
  return pcbffConnector.get(`/policy/${policyCertNo}`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService);
};

export const checkClaimMaintenance = async () => {
  return pcbffConnector.get(`/claim/check-maintenance`)
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getOcrAcceptableRateListData = async () => {
  return pcbffConnector.get(`/claim/ocr-acceptable-rate`)
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getClaimLists = async (user) => {
  return pcbffConnector.get(`/claim/list`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getClaimPolicyLists = async (user) => {
  return pcbffConnector.get(`/claim/policy-list`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getClaimGroupMemberInsures = async (user) => {
  return pcbffConnector.get(`/claim/group-member`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getClaimICD10nHospital = async (user, params) => {
  return pcbffConnector.get(`/claim/icd10nHospital`, { headers: userToAuthHeader(user), params })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getClaimReason = async (user) => {
  return pcbffConnector.get(`/claim/reasons`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getClaimBankList = async (user) => {
  return pcbffConnector.get(`/claim/bank-list`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getClaimBankAccount = async (user) => {
  return pcbffConnector.get(`/claim/bank-account`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const checkDuplicateClaim = async (user, body) => {
  return pcbffConnector.post('/claim/check-duplicate', body, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const submitClaim = async (user, body) => {
  const headers = { ...userToAuthHeader(user), 'Content-Type': 'multipart/form-data' };
  return pcbffConnector.post('/claim/submit', body, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const claimOcrMedReceipt = async (user, formData) => {
  const headers = { ...userToAuthHeader(user), 'Content-Type': 'multipart/form-data' };
  return pcbffConnector.post('/claim/ocr/med-receipt', formData, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const claimOcrMedCertificate = async (user, formData) => {
  const headers = { ...userToAuthHeader(user), 'Content-Type': 'multipart/form-data' };
  return pcbffConnector.post('/claim/ocr/med-certificate', formData, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const claimOcrBookbank = async (user, formData) => {
  const headers = { ...userToAuthHeader(user), 'Content-Type': 'multipart/form-data' };
  return pcbffConnector.post('/claim/ocr/bookbank', formData, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getPolicyLoanPagingList = async (user, page, status) => {
  return pcbffConnector.get(`/loan/policyLists`, { params: { page, status }, headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const submitLoan = async (user, body) => {
  return pcbffConnector.post(`/loan/submission`, body, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const cancelLoan = async (user, params) => {
  const { policyNo, otpNo, otpRef } = params
  return pcbffConnector.post(`/loan/${policyNo}/cancel`, { otpNo, otpRef }, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const rollbackLoan = async (user, policyNo) => {
  return pcbffConnector.post(`/loan/${policyNo}/rollback`, {}, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const uploadFile = async (user, data) => {
  const headers = { ...userToAuthHeader(user), 'Content-Type': 'multipart/form-data' };
  const formData = jsonToFormData(data);
  return pcbffConnector.post(`/file/upload`, formData, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getPolicyListContact = async (user) => {
  return pcbffConnector.get(`/change-contact/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getAddressData = async () => {
  return pcbffConnector.get(`/master-data/addresses`)
    .then(response => response.data)
    .catch(handleErrorService)
}

export const submitChangeContact = async (user, body) => {
  return pcbffConnector.post(`/change-contact/submit`, body, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const checkValidation = async (user, body) => {
  return pcbffConnector.post(`/change-contact/validation`, body, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getCustomerSegment = async (user) => {
  return pcbffConnector.get(`/privilege/customers`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getTier = async (user, params) => {
  return pcbffConnector.get(`/privilege/tier`, { headers: userToAuthHeader(user), params })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getPolicyListBenefit = async (user) => {
  return pcbffConnector.get(`/benefit-payout/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const updatePaymentBenefitSetting = async (user, body) => {
  return pcbffConnector.post(`/benefit-payout/payment-benefit-setting`, body, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getPolicyListDirectdebit = async (user) => {
  return pcbffConnector.get(`/direct-debit/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const updatePaymentCreditSetting = async (user, body) => {
  const headers = { ...userToAuthHeader(user), 'Content-Type': 'multipart/form-data' };
  return pcbffConnector.post(`/direct-debit/payment-credit-setting`, body, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getCostType = async () => {
  return pcbffConnector.get(`/master-data/cost-type`)
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getRelationship = async () => {
  return pcbffConnector.get(`/master-data/relationship`)
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getBankListData = async () => {
  return pcbffConnector.get(`/master-data/banks`)
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getBankAccountByPolicyNo = async (user, policyNo) => {
  return pcbffConnector.get(`/policy/${policyNo}/bank-account`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const submitSpecialReimbursement = async (user, policyNo, formData) => {
  const headers = { ...userToAuthHeader(user), 'Content-Type': 'multipart/form-data' };
  return pcbffConnector.post(`/policy/${policyNo}/reimburse-special`, formData, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getSSOAccessTokenPruServices = async (user, transaction) => {
  const headers = { ...userToAuthHeader(user), 'pru-access-channel': 'pruservices' };
  return pcbffConnector.get(`/auth/sso/token`, { params: { transaction }, headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getSSOAccessTokenPruServicesV2 = async (user, transaction) => {
  const headers = { ...userToAuthHeader(user), 'pru-access-channel': 'pruservicesV2' };
  return pcbffConnector.get(`/auth/sso/token`, { params: { transaction }, headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getSSOAccessTokenPruConnect = async () => {
  const headers = { 'pru-access-channel': 'pruconnect' };
  return pcbffConnector.get(`/auth/sso/token`, { headers, withCredentials: true })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getSSOAccessTokenPruConnectV2 = async () => {
  const headers = { 'pru-access-channel': 'pruconnectV2' };
  return pcbffConnector.get(`/auth/sso/token`, { headers, withCredentials: true })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getSSOAccessTokenEBPortal = async (data) => {
  const headers = { 'pru-access-channel': 'ebportal' };
  return pcbffConnector.post(`/auth/sso/token`, data, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getCustomerInfoByauthSSO = async (channel, token, refreshToken) => {
  const headers = {
    'pru-channel-from': channel,
    'pru-access-token': token,
    'pru-refresh-token': refreshToken
  }
  return pcbffConnector.get(`/auth/sso/client`, { headers })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getListParAnnualStatement = async (user) => {
  return pcbffConnector.get(`/document-download/par-annual-statement/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const downloadParAnnualStatementFile = async (policyNo, filedId, year) => {
  try {
    const filename = `${policyNo}_Annual statement_${year}`;
    const url = `${config.api_endpoint}/document-download/par-annual-statement/download/${filename}?fileId=${filedId}`
    return window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Download error:', error);
    return handleErrorService(error);
  }
}

export const getListTaxCertificate = async (user) => {
  return pcbffConnector.get(`/document-download/premium-certificate/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getListFundDetail = async (user) => {
  return pcbffConnector.get(`/document-download/fund-detail/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const downloadFundDetailFile = async (policyNo, date) => {
  try {
    const filename = `${policyNo}_Fund detail_${date}`;
    const url = `${config.api_endpoint}/document-download/fund-detail/download/${filename}?policyNo=${policyNo}`
    return window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error('Download error:', error);
    return handleErrorService(error);
  }
}

export const getListFundStatement = async (user) => {
  return pcbffConnector.get(`/document-download/fund-statement/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getSurveyInfo = async (user) => {
  return pcbffConnector.get(`/survey`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const submitSurvey = async (user, data) => {
  return pcbffConnector.post(`/survey`, data, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getBigRewardCampaignTime = async () => {
  return pcbffConnector.get(`/campaign/big-reward/check-time-up`)
    .then(response => response.data)
    .catch(handleErrorService)
};

export const registerBigRewardCampaign = async (user, body) => {
  return pcbffConnector.post(`/campaign/big-reward/register`, body, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const logout = async (user) => {
  return pcbffConnector.post(`/auth/logout`, { username: user?.username }, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getIndexPriceLevel = (params) => {
  return pcbffConnector.get('/index-price-level', { params })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getPolicyDocumentList = async (user) => {
  return pcbffConnector.get(`/document-download/policy-document/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getUserConsents = async (user) => {
  return pcbffConnector.get(`/user/consents`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const updateUserConsents = (user, data) => {
  return pcbffConnector.post(`/user/consents`, data, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getMaintenenceCMSFlag = () => {
  return pcbffConnector.get(`/master-data/maintenance-cms-flag`)
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getPrumiumPaymentPolicies = (user) => {
  return pcbffConnector.get(`/premium-payment/policies`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
}

export const getWhiteListLineUIDAccess = async () => {
  return pcbffConnector.get(`/master-data/line-whitelist-access`)
    .then(response => response.data)
    .catch(handleErrorService)
};

export const getUserEkyc = async (user) => {
  return pcbffConnector.get(`/user/ekyc`, { headers: userToAuthHeader(user) })
    .then(response => response.data)
    .catch(handleErrorService)
};